import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Claim } from '../model/claim.model';
import { protectedResources } from 'src/app/auth-config';
import { SecurityRequestService } from './security-request.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly _destroying$ = new Subject<void>();
  public valueSelected = new EventEmitter<any>();

  constructor(
    private msalService: MsalService,
    private router: Router,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalBroadcastService: MsalBroadcastService,
    private httpClient: HttpClient
  ) { }

  public logout(): void {

    this.msalService.logout().subscribe(
      success => {
        console.log(success);
        this.router.navigate([`/login`]);
      },
      error => console.error(error)
    );
  }

  public login(): void {
    const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();

        if (this.msalService.instance.getAllAccounts().length > 0)
          this.router.navigate([`/welcome`]);
      });

    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  public checkAndSetActiveAccount(): void {

    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  public isAuthenticated(): Observable<boolean> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      return of(false);
    }

    return from(this.msalService.acquireTokenSilent({ scopes: ['user.read'] })).pipe(
      map((response: AuthenticationResult) => {
        if (!response.accessToken) {
          return false;
        }

        // Verifica a expiração real do token
        const expiration = response.expiresOn ? response.expiresOn.getTime() : 0;
        const now = new Date().getTime();

        return expiration > now; // Retorna true apenas se o token ainda for válido
      }),
      catchError(error => {
        // Se o erro for relacionado à sessão expirada ou login necessário, limpar cache
        if (error instanceof InteractionRequiredAuthError || error.errorCode === 'invalid_grant') {
          this.logout();
        }

        return of(false);
      })
    );
  }

  public AcquireTokenSilent(): Observable<any> {

    if (this.msalService.instance.getActiveAccount() == null) {
      this.checkAndSetActiveAccount();
    }

    return this.msalService.acquireTokenSilent(protectedResources.todoListApi);
  }

  public AcquireTokenSilentGraph(): Observable<any> {

    if (this.msalService.instance.getActiveAccount() == null) {
      this.checkAndSetActiveAccount();
    }

    return of({
      scopes: [
        "https://graph.microsoft.com/User.Read"
      ]
    }).pipe(
      switchMap(request => this.msalService.acquireTokenSilent(request))
    )
  }

  public getAuthorization(): void {

    of({})
      .pipe(
        switchMap(request => this.AcquireTokenSilent()),
        map(request => {
          return {
            headers: new HttpHeaders().set('Authorization', `Bearer ${request.accessToken}`)
          };
        }),
        switchMap(options => this.httpClient.request("GET", `${environment.bffApi}/authorization`, options)),
        catchError(error => {
          if (error.status === 401) {
            return this.router.navigateByUrl('/login');
          }
          return of(null);
        })
      ).subscribe(
        claims => {
          localStorage.setItem('claims', JSON.stringify(claims));
          this.valueSelected.next(claims);

          if (this.router.url === "/login")
            this.router.navigate([`/welcome`]);

        },
        error => console.error(error)
      )
  }

  public getClaims(): Claim {
    var _claims = localStorage.getItem("claims");

    if (_claims != null)
      return JSON.parse(_claims) as Claim;
    else
      new Claim();
  }

  public toClean(): void {
    localStorage.removeItem("claims");
  }
}
